import { BarDatum } from "@nivo/bar";
import { Datum } from "@nivo/line";
import { SorterResult, SortOrder } from "antd/es/table/interface";
import {
  BasePagination,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { WidgetCardNoMap, WidgetStats } from "api/interfaces/widgetInterface.interface";
import { barKeyName } from "components/atoms/chart/BarChart";
import { changeSortOrderString } from "components/atoms/table/AntdTable";
import { Layout } from "react-grid-layout";
import { COLORS } from "styles/colors";
import { baseQueryInfo } from "./queryUtil";
import { RequiredPermissionType } from "./MenuUtil";

export type AssignModalType = {
  [key: string]: boolean;
};
export const cardInitialLayout: Layout[] = [
  { i: "organizationCount", x: 0, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
  { i: "recorderCount", x: 1, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
  { i: "cameraCount", x: 2, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
  { i: "alertCount", x: 3, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
];
export const chartInitialLayout: Layout[] = [
  // { i: "installation", x: 0, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
  // { i: "revenue", x: 0, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
  // { i: "alertList", x: 1, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
  { i: "storage", x: 0, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
  { i: "recorder", x: 1, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
  { i: "loginHistory", x: 1, y: 1, w: 1, h: 1, minW: 1, minH: 1 },
  { i: "alertFeed", x: 0, y: 1, w: 1, h: 1, minW: 1, minH: 1 },
  // { i: "alertHistory", x: 0, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
  // { i: "cvvDetection", x: 1, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
  // { i: "inventory", x: 1, y: 0, w: 1, h: 1, minW: 1, minH: 1 },
];

export const initialCardData: WidgetCardNoMap = {
  count: 0,
  details: [],
  stats: [],
};

export const commonPaginationPerPage = 6;
export const pageMaxLimit = (height: number) => {
  if (height >= 2) {
    return commonPaginationPerPage * height >= 99
      ? 99
      : commonPaginationPerPage * height;
  } else {
    return commonPaginationPerPage;
  }
};

export const changeStatsToBarDatum = (data: WidgetStats[] | undefined) => {
  let tempData: BarDatum[] = data
    ? data.map((stat) => {
        return {
          key: stat.date,
          indexName: stat.date,
          [barKeyName]: stat.count,
          [`${barKeyName}Color`]: COLORS.PROGRESSBAR,
        };
      })
    : [];
  return tempData;
};

export const changeStatsToLine = (data: WidgetStats[] | undefined) => {
  let tempData: Datum[] = data
    ? data.map((stat) => {
        return {
          x: stat.date,
          y: stat.count,
        };
      })
    : [];
  return tempData;
};

// 데이터의 최대값을 구합니다
const getMaxValue = (data: BarDatum[]) => {
  return Math.max(...data.map((d) => Number(d.barChartKey)));
};

// 최대값을 기반으로 적절한 눈금 값을 생성합니다.
const getTickValues = (maxValue: number) => {
  // 적절한 단위를 선택합니다
  const unit = Math.pow(10, Math.floor(Math.log10(maxValue))); // 데이터의 규모에 따라 단위 결정
  const step = Math.ceil(maxValue / unit); // 눈금 간격을 설정
  return Array.from({ length: step + 1 }, (_, i) => i * unit);
};

export const calTickValues = (data: BarDatum[]) => {
  return data.length === 0 ? undefined : getTickValues(getMaxValue(data));
};

export const getAxisBottomValue = (data: BarDatum[], layoutWidth: number) => {
  return data
    .map((d, idx) => {
      if (layoutWidth <= 1) {
        if (data.length > 6 && idx % 2 !== 0) {
          return null;
        }
      }

      return d.indexName;
    })
    .filter((v) => v !== null);
};

export const getMaxYValue = (data: Datum[]): number => {
  // y 값이 null이 아닌 요소들로 배열을 만든 후, 최대값을 계산
  const validYValues = data
    .map((d) => d.barChartKey) // y 값만 추출
    .filter((y) => y !== null) as number[]; // null이 아닌 값들만 필터링

  // 유효한 y 값이 없으면 null 반환
  // if (validYValues.length === 0) {
  //   return 0;
  // }

  // Math.max를 사용하여 최대값 반환
  return validYValues.length === 0 || Math.max(...validYValues) === 0
    ? 10
    : Math.max(...validYValues);
};

export const changeSortDirection = (
  sorter: SorterResult<any>,
  queryInfo: BasePagination
) => {
  console.log(
    sorter.columnKey,
    sorter.order,
    queryInfo.sortType,
    queryInfo.sortDirection
  );
  let tempDirection: SortOrder = "ascend";
  if (sorter.order === undefined) {
    if (queryInfo.sortDirection === SORT_DIRECTION.ASC) {
      tempDirection = "descend";
    } else {
      tempDirection = "ascend";
    }
  } else {
    tempDirection = sorter.order;
  }
  return changeSortOrderString(tempDirection);
};

export const recorderInitialQueryInfo = {
  ...baseQueryInfo,
  pageLimit: commonPaginationPerPage,
  sortType: "installedDate",
  sortDirection: SORT_DIRECTION.DESC,
};

export const cameraInitialQueryInfo = {
  ...baseQueryInfo,
  pageLimit: commonPaginationPerPage,
  sortType: "mergedSystemName",
  sortDirection: SORT_DIRECTION.ASC,
};

export const keyTitle: { [key in TableItemName]: string } = {
  installation: "Device Installation History",
  revenue: "Monthly Revenue History",
  recorder: "Recorders",
  alertFeed: "Alert Feed",
  alertList: "Alert List",
  alertHistory: "Alert History",
  cvvDetection: "CVV Detections",
  inventory: "Inventory",
  storage: "Cloud Storage Use",
  loginHistory: "Login History",
};

export type TableItemName =
  | "installation"
  | "revenue"
  | "recorder"
  | "alertFeed"
  | "alertList"
  | "alertHistory"
  | "cvvDetection"
  | "inventory"
  | "storage"
  | "loginHistory";

export const tableItemList: Record<TableItemName, string> = {
  recorder: "Recorders",
  alertFeed: "Alert Feed",
  alertList: "Alert List",
  alertHistory: "Alert History",
  cvvDetection: "CVV Detections",
  inventory: "Inventory",
  loginHistory: "Login History",
  storage: "Cloud Storage Use",
  revenue: "Revenue",
  installation: "Device Installations",
} as const;

export const tableItemPermission: Record<
  TableItemName,
  RequiredPermissionType
> = {
  recorder: ["viewRecordersAndGroups"],
  alertFeed: ["viewAlerts"],
  alertList: ["viewAlerts"],
  alertHistory: ["viewAlerts"],
  cvvDetection: ["viewReports"],
  inventory: ["viewReports"],
  loginHistory: ["viewUsersAndGroups"],
  storage: "rw",
  revenue: ["viewReports"],
  installation: ["viewReports"],
};

export const isDisabledDashboardTableList = [
  "revenue",
  "installation",
  // "storage",
];

/** Layout 배열에서 동일 key(i) 중복 제거 - React key 경고 방지 */
export const deduplicateLayoutByKey = (layout: Layout[]): Layout[] => {
  const seen = new Set<string>();
  return layout.filter((d) => {
    if (seen.has(d.i)) return false;
    seen.add(d.i);
    return true;
  });
};

/**
 * minW/minH가 w/h(또는 현재 breakpoint cols)보다 크면 GridItem 경고 발생 → 그리드에 넘기기 전 정규화
 * @param cols 현재 breakpoint 컬럼 수 (지정 시 w/minW가 cols를 넘지 않도록 함)
 */
export const normalizeLayoutMinSize = (
  layout: Layout[],
  cols?: number
): Layout[] => {
  return layout.map((item) => {
    const rawW = item.w ?? 1;
    const w = cols != null ? Math.min(rawW, cols) : rawW;
    const h = item.h ?? 1;
    const maxW = cols != null ? Math.min(rawW, cols) : rawW;
    const minW =
      item.minW != null ? Math.min(item.minW, w, maxW) : undefined;
    const minH = item.minH != null ? Math.min(item.minH, h) : undefined;
    return {
      ...item,
      w,
      h,
      ...(minW != null && { minW }),
      ...(minH != null && { minH }),
    };
  });
};

/** y, x 순 정렬 후 왼쪽 위부터 채우기 (빈 칸 없이 재배치) */
export const compactLayout = (layout: Layout[], cols: number): Layout[] => {
  const sorted = [...layout].sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    return a.x - b.x;
  });
  return sorted.map((item, index) => ({
    ...item,
    x: index % cols,
    y: Math.floor(index / cols),
  }));
};

